/**
 * /auth/return — adopts a centralized-login token from the URL query or
 * fragment, strips the token-bearing URL, and resumes the stashed deep link.
 * A handoff without a product-admin grant ends at /auth/no-access.
 */
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { popDeepLink } from '@/auth/silentRefresh';
import { getAccessToken, getClaims } from '@/auth/authStore';
import { useAuth } from '@/auth/AuthContext';
import { useCopy } from '@/hooks/useCopy';

export function AuthReturn() {
  const navigate = useNavigate();
  const { setAuthed } = useAuth();
  const ran = useRef(false);
  const { t } = useCopy();

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (getAccessToken()) {
      const deep = popDeepLink() ?? '/';
      navigate(deep, { replace: true });
      return;
    }

    const qs = new URLSearchParams(window.location.search);
    const hs = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const pick = (key: string) => qs.get(key) ?? hs.get(key);
    const accessToken = pick('accessToken') ?? pick('token');
    const expiresIn = pick('expiresIn');
    const errorParam = pick('error');

    if (accessToken) {
      const seconds = Number(expiresIn);
      const expiresAtDate =
        Number.isFinite(seconds) && seconds > 0 ? new Date(Date.now() + seconds * 1000) : null;
      const expiresAt =
        expiresAtDate && Number.isFinite(expiresAtDate.getTime())
          ? expiresAtDate.toISOString()
          : null;

      window.history.replaceState(null, '', window.location.pathname);
      setAuthed(accessToken, expiresAt);

      if (!getClaims()?.productAdminOf) {
        navigate('/auth/no-access', { replace: true });
        return;
      }

      const deep = popDeepLink() ?? '/';
      navigate(deep, { replace: true });
      return;
    }

    if (errorParam || !getAccessToken()) {
      navigate('/auth/no-access', { replace: true });
    }
  }, [navigate, setAuthed]);

  // No sync guard here: the effect covers both the "token already in store"
  // path and the URL-token adoption path. A brief loading flash on
  // /auth/return is acceptable — this route is only ever reached via the
  // OAuth callback redirect, never bookmarked directly.
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        gap: 2,
      }}
    >
      <LoadingSpinner />
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
        }}
      >
        {t('auth.returning', 'Completing sign-in…')}
      </Typography>
    </Box>
  );
}
